import { FormGroup, FormParagraph, List } from '@ksyos/design-system';
import { useMemo } from 'react';
import type { IOrthOV1DiagnosticReportCustomControlDataBevindingen } from '../../../../../common/validation/customControls/ophthalmology';
import type { ICustomControlDiagnosticReportReadProps } from '../interfaces/customControlDiagnosticReportReadProps';
import { getServiceLabel } from '../../../../../common/customControls/getServiceLabel';
import { GradingSummary } from '../../../../../common/enums/gradingSummary';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { orthODiagnosticReportCustomControlBevindingenLabels } from '../../../../../common/validation/customControls/ophthalmology';
import { translateKey } from '../../../../translate';
import { ProcessContainer } from '../../episodes/common';
import { ProcessItemFooter } from '../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../utils/v1/CustomControlCollapseBody';

export function OrthOV1DiagnosticReportBevindingenRead(props: ICustomControlDiagnosticReportReadProps) {
    const bevindingenList = useMemo(
        () => renderBevindingen(props.diagnosticReport.customControlData.bevindingen),
        [props.diagnosticReport.customControlData.bevindingen]
    );

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                status={
                    props.diagnosticReport.gradingSummary === GradingSummary.deviant
                        ? translateKey('diagnosis.deviant')
                        : undefined
                }
                title={getServiceLabel(
                    props.process.code,
                    'diagnosticReport',
                    translateKey('diagnosticReport.findings')
                )}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.diagnosticReport.customControlData.bevindingen &&
                    Object.values(props.diagnosticReport.customControlData.bevindingen).includes(true) && (
                        <FormGroup label="Bevindingen">
                            <List>{bevindingenList}</List>
                        </FormGroup>
                    )}
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideExaminationReport')}
                    labelMore={translateKey('diagnosticReport.showExaminationReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    function renderBevindingen(bevindingen: IOrthOV1DiagnosticReportCustomControlDataBevindingen) {
        return Object.entries(bevindingen)
            .filter((bevinding) => bevinding[1] && bevinding[0] !== 'anders')
            .map((bevinding) => {
                return (
                    <FormParagraph key={bevinding[0]}>
                        {bevinding[0] === 'andersText'
                            ? bevinding[1]
                            : orthODiagnosticReportCustomControlBevindingenLabels[bevinding[0]]}
                    </FormParagraph>
                );
            });
    }
}
